import { Drawer, Spin } from "antd";
import { memo, useCallback, useEffect, useState } from "react";
import CreateChatForm from "./CreateChatForm";
import { useSendMessage, usePromoteToMyInbox } from "queries/conversation";
import { useDispatch, useSelector } from "react-redux";
import { setLastMessageId } from "appredux/chatSlice";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { selectedPhoneNumberSelector } from "appredux/selectors";
import { useLocation, useNavigate } from "react-router";
import { buildConversationMessagePayload } from "utils/buildConversationMessagePayload";

const CreateChatComp = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedPhoneNumber = useSelector(selectedPhoneNumberSelector);
  const { id: phoneNumberId } = selectedPhoneNumber || {};
  // Channel inhouse (tanpa waba_id) tidak punya nomor WhatsApp, jadi chat
  // baru hanya bisa ke kontak, tidak bisa ke nomor bebas.
  const isInhouseChannel =
    !!selectedPhoneNumber && !selectedPhoneNumber.waba_id;
  const [open, setOpen] = useState(false);
  const mutation = useSendMessage();
  const promoteToMyInbox = usePromoteToMyInbox();
  const location = useLocation();

  const closeHandler = useCallback(() => {
    setOpen(false);
    // Selalu kembali ke list percakapan channel yang sedang dibuka — bukan
    // navigate(-1) yang bisa melempar ke URL asal yang tak relevan (deep link,
    // refresh, dari halaman lain). Drawer ini selalu di dalam /conversations/:id.
    setTimeout(() => navigate(`/conversations/${phoneNumberId}/list`), 100);
  }, [navigate, phoneNumberId]);

  const submitHandler = useCallback(
    (values) => {
      const payload = buildConversationMessagePayload(values, phoneNumberId);
      // Inhouse: tujuan dikirim via external_handle kontak (lihat CreateChatForm).
      if (isInhouseChannel) {
        payload.to_phone_number = values.to_phone_number;
      }
      mutation.mutate(payload, {
        onSuccess: async ({ data }) => {
          const lastMessageId = data?.data?.message_id;
          // Response create chat hanya membawa conversation_id (tanpa objek
          // conversation), baik conv baru maupun yang sudah ada. Promosikan ke
          // my_inbox via id: pindah tab + getDetail SEGERA + tulis cache + fokus.
          const conversationId = data?.data?.conversation_id;
          if (conversationId) {
            dispatch(setLastMessageId(lastMessageId));
            promoteToMyInbox(conversationId);
          }
          if (location.state?.id) {
            setOpen(false);
            setTimeout(
              () => navigate(`/conversations/${phoneNumberId}/list`),
              100,
            );
          } else {
            closeHandler();
          }
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      closeHandler,
      dispatch,
      isInhouseChannel,
      mutation,
      phoneNumberId,
      location.state?.id,
      navigate,
      promoteToMyInbox,
    ],
  );

  useEffect(() => {
    setOpen(true);
  }, []);

  return (
    <Drawer
      title={
        isInhouseChannel
          ? t("inhouse_channel.send_message")
          : t("send_wa_messages")
      }
      placement="left"
      getContainer={false}
      width="100%"
      onClose={closeHandler}
      destroyOnHidden
      styles={{
        header: {
          background: "#FAFAFA",
          paddingTop: 19.5,
          paddingBottom: 19.5,
          borderLeft: "1px solid #E6E7EB",
        },
      }}
      open={open}
      {...props}
    >
      <Spin
        indicator={<LoadingOutlined spin />}
        spinning={mutation.isPending}
        tip="Loading..."
        size="large"
      >
        <CreateChatForm
          onCancel={closeHandler}
          onSubmit={submitHandler}
          state={location.state}
          contactOnly={isInhouseChannel}
          inhouseChannelId={isInhouseChannel ? phoneNumberId : undefined}
        />
      </Spin>
    </Drawer>
  );
};

const CreateChat = memo(CreateChatComp);

export default CreateChat;
